import { MutableRefObject } from 'react';
import { School, Enrollment, TestScoreRequirements } from '@common-types';
import { CoreBox, CoreChip, CoreTypography } from '@youscience/core';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { clamp, round } from 'lodash-es';

import { separateDigitsByComma } from '@utils/formatter';

import UrlIcon from '@images/url.svg?react';
import CommonAppIcon from '@images/common-app.svg?react';
import CheckMarkIcon from '@images/checkmark-in-cercle.svg?react';

import { CircularProgress } from './CircularProgress';

import {
  sxStyles,
  StyledAdmissionsWrap,
  StyledAdmissionsInner,
  StyledGeneralWrap,
  StyledGeneralInfo,
  StyledAdmissionWebsiteLink,
  StyledOtherRequirementsInner,
  StyledOpenAdmissionBox,
  StyledGeneralDivider,
  StyledReportingYear,
  StyledTestRequirementsInner,
  StyledTestRequirementBlockTitles,
  StyledFilledPercent,
  StyledScoreScale,
  StyledLeftRange,
  StyledRightRange,
  StyledDemographic,
  StyledChartLegendWrap,
  StyledChartLegendGroup,
  StyledChartLegendItem,
} from './Admissions.styles';
import { PageSectionIds } from '@constants/schoolDetailsPage';
import { SectionLinkOffset } from '@pages/SchoolDetails/SchoolDetails.styles';

ChartJS.register(ArcElement, Tooltip);

const SKIPPED_FIELDS: string[] = ['totalStudentsEnrolled', 'totalStudentsMen', 'totalStudentsWomen'];

const CHART_INFO: { [k: string]: { color: string; label: string } } = {
  totalStudentsWhite: { color: '#FFC107', label: 'White' },
  totalStudentsLatino: { color: '#198754', label: 'Hispanic or Latino' },
  totalStudentsAfricanAmerican: { color: '#FD7E14', label: 'Black or African American' },
  totalStudentsPacificIslander: { color: '#0DCAF0', label: 'Native Hawaiian or Other' },
  totalStudentsNativeAmerican: { color: '#0D6EFD', label: 'American Indian or Native Alaska' },
  totalStudentsTwoOrMoreRaces: { color: '#20C997', label: 'Two or more races ' },
  totalStudentsAsian: { color: '#6610F2', label: 'Asian' },
  other: { color: '#D63384', label: 'Other' },
};

const PIE_CHART_OPTIONS = {
  responsive: true,
  plugins: {
    tooltip: {
      backgroundColor: '#212121',
      displayColors: false,
      callbacks: {
        label: (context: { label: string }) => context.label,
      },
    },
  },
};

const getGraphicData = (data: Enrollment) => {
  if (!data || !Object.keys(data).length) {
    return null;
  }

  const labels: string[] = [];
  const legendLabels: { color: string; label: string }[] = [];
  const values: number[] = [];
  const valueColors: string[] = [];
  let runningTotal = 0;

  Object.entries(data)
    .sort((a, b) => b[1] - a[1])
    .forEach(([key, value]) => {
      if (SKIPPED_FIELDS.includes(key) || !value) {
        return;
      }

      const total = value;

      if (total > 0) {
        const percentage = !data.totalStudentsEnrolled ? 0 : round((total / data.totalStudentsEnrolled) * 100, 2);

        runningTotal += percentage;

        let { label } = CHART_INFO[key];

        if (label.length > 22) {
          label = `${label.slice(0, 22)}...`;
        }

        labels.push(`${percentage}% ${label}`);
        legendLabels.push(CHART_INFO[key]);
        values.push(percentage);
        valueColors.push(CHART_INFO[key].color);
      }
    });

  const otherPercentage = round(100 - runningTotal, 2);

  if (otherPercentage > 0) {
    labels.push(`${otherPercentage}% ${CHART_INFO.other.label}`);
    legendLabels.push(CHART_INFO.other);
    values.push(otherPercentage);
    valueColors.push(CHART_INFO.other.color);
  }

  return {
    labels,
    legendLabels,
    datasets: [
      {
        data: values,
        backgroundColor: valueColors,
        borderColor: valueColors,
        borderWidth: 0,
      },
    ],
  };
};

const MAX_LEGEND_IN_COLUMN = 4;

const ACT_SCORE_SCALE = { min: 1, max: 36 };
const SAT_SECTION_SCORE_SCALE = { min: 200, max: 800 };

const TEST_SCORE_REPORTING_YEAR = 2024;

interface TestScoreRequirementInfo {
  label: string;
  description: string;
  chipVariant: keyof typeof sxStyles.testScoreRequirementChip;
}

const NOT_REPORTED_TEST_SCORE_INFO: TestScoreRequirementInfo = {
  label: 'Not reported',
  description: "Check the college's admissions page for current requirements.",
  chipVariant: 'neutralOutlined',
};

const OPEN_ADMISSION_TEST_SCORE_INFO: TestScoreRequirementInfo = {
  label: 'Not needed to be admitted',
  description: "Scores aren't used for admission. Some programs may still ask for placement testing.",
  chipVariant: 'neutral',
};

const TEST_SCORE_REQUIREMENT_INFO: Partial<Record<TestScoreRequirements, TestScoreRequirementInfo>> = {
  [TestScoreRequirements.REQUIRED]: {
    label: 'Required',
    description: "You'll need to send scores from either test with your application.",
    chipVariant: 'filled',
  },
  [TestScoreRequirements.RECOMMENDED]: {
    label: 'Recommended',
    description: 'Not required, but this college encourages you to send scores.',
    chipVariant: 'outlined',
  },
  [TestScoreRequirements.OPTIONAL]: {
    label: 'Optional',
    description: "You choose whether to send scores. They're reviewed if you do.",
    chipVariant: 'soft',
  },
  [TestScoreRequirements.NOT_PART_OF_THE_DECISION]: {
    label: 'Not part of the decision',
    description: "This college reported that it doesn't use test scores to admit students.",
    chipVariant: 'neutral',
  },
};

const getTestScoreRequirementInfo = (requirement?: TestScoreRequirements) => {
  if (requirement === undefined) return OPEN_ADMISSION_TEST_SCORE_INFO;

  return TEST_SCORE_REQUIREMENT_INFO[requirement] ?? NOT_REPORTED_TEST_SCORE_INFO;
};

const renderTestItem = (
  title: string,
  leftRangeScore: number,
  rightRangeScore: number,
  scale: { min: number; max: number },
) => {
  const toPercent = (score: number) => clamp(((score - scale.min) / (scale.max - scale.min)) * 100, 0, 100);

  const leftOffset = toPercent(leftRangeScore);
  const rangeWidth = Math.max(toPercent(rightRangeScore) - leftOffset, 0);

  return (
    <CoreBox sx={sxStyles.testScoreItem}>
      <CoreTypography component="div" variant="body2">
        {title}
      </CoreTypography>

      <CoreBox>
        <CoreBox sx={sxStyles.testItem}>
          <StyledFilledPercent leftOffset={leftOffset} rangeWidth={rangeWidth}>
            <StyledLeftRange>
              <CoreTypography sx={sxStyles.leftRangeScore} component="div" variant="caption">
                {leftRangeScore}
              </CoreTypography>

              <CoreTypography sx={sxStyles.leftPercentRange} component="div" variant="caption">
                25%
              </CoreTypography>
            </StyledLeftRange>

            <StyledRightRange>
              <CoreTypography sx={sxStyles.rightRangeScore} component="div" variant="caption">
                {rightRangeScore}
              </CoreTypography>

              <CoreTypography sx={sxStyles.rightPercentRange} component="div" variant="caption">
                75%
              </CoreTypography>
            </StyledRightRange>
          </StyledFilledPercent>
        </CoreBox>

        <StyledScoreScale>
          <CoreTypography component="div" variant="caption">
            {scale.min}
          </CoreTypography>

          <CoreTypography component="div" variant="caption">
            {scale.max}
          </CoreTypography>
        </StyledScoreScale>
      </CoreBox>
    </CoreBox>
  );
};

const renderStatisticItem = (title: string, value?: string) => {
  if (!value) return '';

  return (
    <CoreBox sx={sxStyles.statisticItemWrap}>
      <CoreTypography sx={sxStyles.statisticItemTitle} component="div" variant="body2">
        {title}
      </CoreTypography>

      <CoreTypography component="div" variant="h6">
        {value}
      </CoreTypography>
    </CoreBox>
  );
};

interface AdmissionsProps {
  scrollToRef: MutableRefObject<HTMLAnchorElement | null>;
  school: School;
}

export const Admissions = (props: AdmissionsProps) => {
  const {
    scrollToRef,
    school: { memberId, collegeScorecard, directory, admissions, enrollment },
  } = props;

  const chartData = getGraphicData(enrollment);
  const rateValue = collegeScorecard
    ? +Math.round((collegeScorecard?.latest?.admissions?.admission_rate?.overall || 0) * 100)
    : 0;
  const hasActEnglishData = admissions?.actEnglish25Score && admissions?.actEnglish75Score;
  const hasActMathData = admissions?.actMath25Score && admissions?.actMath75Score;
  const hasSatReadingWritingData = admissions?.satReadingAndWriting25Score && admissions?.satReadingAndWriting75Score;
  const hasSatMathData = admissions?.satMath25Score && admissions?.satMath75Score;

  const hasActData = hasActMathData || hasActEnglishData;
  const hasSatData = hasSatReadingWritingData || hasSatMathData;

  const isOpenAdmission = admissions?.testScoreRequirements === undefined;
  const testScoreRequirementInfo = getTestScoreRequirementInfo(admissions?.testScoreRequirements);

  const schoolUrl = directory?.links.adminUrl;
  const admissionLink = schoolUrl?.match(/^[a-zA-Z]+:\/\//) ? schoolUrl : `https://${schoolUrl}`;
  const enrollmentNumber = `${separateDigitsByComma.format(enrollment?.totalStudentsEnrolled || 0)} students`;

  const highSchoolGPA = admissions?.hasHSGPAAdmissionRequirements ? 'Required' : 'Not Required';
  const highSchoolTranscript = admissions?.hasTranscriptAdmissionRequirements ? 'Required' : 'Not Required';
  const highSchoolRank = admissions?.hasHSRankAdmissionRequirements ? 'Required' : 'Not Required';
  const highSchoolRecommendations = admissions?.hasRecommendationAdmissionRequirements ? 'Required' : 'Not Required';

  const preparedChartInfo = chartData?.legendLabels ?? [];

  const isOneColumn = preparedChartInfo.length <= MAX_LEGEND_IN_COLUMN;

  const renderChartLegendGroup = (from: number, to?: number) => (
    <StyledChartLegendGroup isOneColumn={isOneColumn}>
      {preparedChartInfo.slice(from, to).map(({ label, color }) => {
        return (
          <StyledChartLegendItem component="div" variant="body2" color={color} key={color}>
            {label}
          </StyledChartLegendItem>
        );
      })}
    </StyledChartLegendGroup>
  );

  return (
    <CoreBox sx={sxStyles.admissionsContainer}>
      <SectionLinkOffset id={PageSectionIds['admissions']} ref={scrollToRef} />

      <CoreTypography sx={sxStyles.admissionsSectionTitle} variant="h4" component="h3">
        Admissions
      </CoreTypography>

      <StyledAdmissionsWrap>
        <CoreTypography sx={sxStyles.keyStatisticsTitle} variant="h5" component="h4">
          Key statistics
        </CoreTypography>

        <StyledAdmissionsInner>
          <StyledGeneralWrap>
            {isOpenAdmission ? (
              <StyledOpenAdmissionBox>
                <CheckMarkIcon />

                <CoreTypography sx={sxStyles.openAdmissionTitle} component="div" variant="body2">
                  Open admission
                </CoreTypography>

                <CoreTypography sx={sxStyles.openAdmissionDescription} component="p" variant="body2">
                  This college accepts nearly all applicants with a high school diploma or equivalent.
                </CoreTypography>
              </StyledOpenAdmissionBox>
            ) : (
              <CoreBox sx={sxStyles.rateGraphicWrap}>
                <CircularProgress filling={rateValue} title="Admission Rate" />
              </CoreBox>
            )}

            <StyledGeneralInfo>
              <CoreTypography sx={sxStyles.generalTitle} component="div" variant="overline">
                General
              </CoreTypography>

              <CoreTypography sx={sxStyles.testScoreRequirementLabel} component="div" variant="body2">
                SAT or ACT scores
              </CoreTypography>

              <CoreChip
                data-testid="testScoreRequirement"
                sx={sxStyles.testScoreRequirementChip[testScoreRequirementInfo.chipVariant]}
                label={testScoreRequirementInfo.label}
              />

              <CoreTypography sx={sxStyles.testScoreRequirementDescription} component="p" variant="body2">
                {testScoreRequirementInfo.description}
              </CoreTypography>

              <StyledReportingYear>
                {`Reported by the college for fall ${TEST_SCORE_REPORTING_YEAR} · policies can change`}
              </StyledReportingYear>

              <StyledGeneralDivider />

              {renderStatisticItem('Enrollment number', enrollmentNumber)}

              <StyledGeneralDivider />

              {schoolUrl?.length > 1 && (
                <StyledAdmissionWebsiteLink target="_blank" href={admissionLink}>
                  <UrlIcon />
                  Check current requirements
                </StyledAdmissionWebsiteLink>
              )}

              {memberId ? <CommonAppIcon /> : null}
            </StyledGeneralInfo>
          </StyledGeneralWrap>

          {(hasActData || hasSatData) && (
            <CoreBox sx={sxStyles.testRequirementWrap}>
              <CoreTypography component="div" variant="overline">
                Test Score History of Admitted Students
              </CoreTypography>

              <CoreTypography sx={sxStyles.testScoreDescription} component="p" variant="body2">
                This range represents the scores of the middle 50% of admitted students. It can give you a sense of how
                your SAT or ACT score compares with other students who were admitted.
              </CoreTypography>

              <StyledTestRequirementsInner>
                {hasActData && (
                  <CoreBox sx={sxStyles.testRequirementBlock}>
                    <StyledTestRequirementBlockTitles>
                      <CoreTypography component="div" variant="body2">
                        ACT
                      </CoreTypography>
                    </StyledTestRequirementBlockTitles>

                    {hasActEnglishData &&
                      renderTestItem(
                        'English',
                        admissions?.actEnglish25Score || 0,
                        admissions?.actEnglish75Score || 0,
                        ACT_SCORE_SCALE,
                      )}

                    {hasActMathData &&
                      renderTestItem(
                        'Math',
                        admissions?.actMath25Score || 0,
                        admissions?.actMath75Score || 0,
                        ACT_SCORE_SCALE,
                      )}
                  </CoreBox>
                )}

                {hasSatData && (
                  <CoreBox sx={sxStyles.testRequirementBlock}>
                    <StyledTestRequirementBlockTitles>
                      <CoreTypography component="div" variant="body2">
                        SAT
                      </CoreTypography>
                    </StyledTestRequirementBlockTitles>

                    {hasSatReadingWritingData &&
                      renderTestItem(
                        'Reading/writing',
                        admissions?.satReadingAndWriting25Score || 0,
                        admissions?.satReadingAndWriting75Score || 0,
                        SAT_SECTION_SCORE_SCALE,
                      )}

                    {hasSatMathData &&
                      renderTestItem(
                        'Math',
                        admissions?.satMath25Score || 0,
                        admissions?.satMath75Score || 0,
                        SAT_SECTION_SCORE_SCALE,
                      )}
                  </CoreBox>
                )}
              </StyledTestRequirementsInner>
            </CoreBox>
          )}

          <CoreBox sx={sxStyles.otherRequirementsWrap}>
            <CoreTypography component="div" variant="overline">
              Other requirements
            </CoreTypography>

            <StyledOtherRequirementsInner>
              {renderStatisticItem('High school GPA', highSchoolGPA)}

              {renderStatisticItem('High school transcript', highSchoolTranscript)}

              {renderStatisticItem('High school rank', highSchoolRank)}

              {renderStatisticItem('Recommendations', highSchoolRecommendations)}
            </StyledOtherRequirementsInner>
          </CoreBox>

          {chartData?.labels && chartData.labels.length > 0 && (
            <StyledDemographic>
              <CoreBox sx={sxStyles.pieChart}>
                <Pie options={PIE_CHART_OPTIONS} data={chartData} />
              </CoreBox>

              <CoreBox sx={sxStyles.pieChartLegend}>
                <CoreTypography variant="overline" component="h5">
                  Demographic information of the student body
                </CoreTypography>

                <StyledChartLegendWrap>
                  {renderChartLegendGroup(0, MAX_LEGEND_IN_COLUMN)}

                  {renderChartLegendGroup(MAX_LEGEND_IN_COLUMN)}
                </StyledChartLegendWrap>
              </CoreBox>
            </StyledDemographic>
          )}
        </StyledAdmissionsInner>
      </StyledAdmissionsWrap>
    </CoreBox>
  );
};
